import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Menu, X, ArrowRight, Zap, KeyRound, Layers, Boxes, Check, Star } from "lucide-react";
import CookieConsent from "@/components/CookieConsent";
import { ServiceInfoModal } from "@/components/ServiceInfoModal";

const NEON = "#E3FF00";
const btn = "bg-[#E3FF00] text-neutral-900 font-display font-bold rounded-xl shadow-sm hover:brightness-95 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-2";

const HERO_IMG = "https://images.unsplash.com/photo-1635776062127-d379bfcba9f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwZ3JhZGllbnR8ZW58MHx8fHwxNzkxMTQ5OTI0fDA&ixlib=rb-4.1.0&q=85";
const WORK_IMG = "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3klMjB3YXZlc3xlbnwwfHx8fDE3OTExNDk5MjR8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_SERVICES = [
  { key: "homesmart", name: "Home Smart", description: "Immobili, pubblicazioni su HolidayWorld e consumi del mese.", logo_url: "/home-smart-logo.png", status: "active" },
  { key: "infinity", name: "Infinity", description: "Fornitore luce e gas — sconti e cashback sulle bollette.", logo_url: "/infinity-logo.png", status: "active" },
  { key: "comparapro", name: "Comparapro", description: "Confronta offerte e risparmia sui tuoi servizi.", logo_url: "/comparapro-logo.png", logo_bg: "#18181b", status: "active" },
  { key: "airep", name: "AI-Report", description: "Sondaggi live, nuvole di parole e domande al pubblico con report finale creato dall'AI.", logo_url: "/airep-logo.svg", logo_bg: "#ffffff", status: "active" },
  { key: "zonepass", name: "Zonepass", description: "In arrivo.", logo_url: "/zonepass-logo.png", status: "coming_soon" },
  { key: "segnalo", name: "iosegnalo.net", description: "In arrivo.", logo_url: "/segnalo-logo.png", status: "coming_soon" },
  { key: "hometob", name: "HomeToB.it", description: "In arrivo.", logo_url: "/hometob-logo.png?v=2", status: "coming_soon" },
];

const Brand = ({ className = "" }) => (
  <div className={`flex items-center gap-2.5 ${className}`}>
    <span className="h-11 w-11 rounded-2xl grid place-items-center font-display font-extrabold text-xl text-neutral-900" style={{ backgroundColor: NEON }}>MP</span>
    <span className="font-display font-extrabold text-xl text-neutral-900 tracking-tight">Mypasspartu.it</span>
  </div>
);

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { label: "Cos'è", href: "#cos-e" },
    { label: "Servizi", href: "#partner", testid: "nav-services-link" },
    { label: "Come funziona", href: "#come-funziona" },
    { label: "Candida servizio", href: "#candida", testid: "nav-apply-link" },
  ];
  return (
    <header data-testid="public-navbar" className={`fixed top-0 inset-x-0 z-50 transition-colors ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-brand-link"><Brand /></a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testid} className="text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="hidden sm:flex items-center gap-3">
          <Link to="/register" data-testid="nav-register-button" className="text-sm font-semibold text-neutral-900 px-4 py-2.5 rounded-xl border border-neutral-300 hover:bg-neutral-100 transition-colors">Registrati</Link>
          <Link to="/login" data-testid="nav-login-button" className={`${btn} text-sm px-5 py-2.5`}>Accedi</Link>
        </div>
        <button className="sm:hidden p-2 text-neutral-900" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="sm:hidden bg-white border-b border-neutral-200 px-5 py-4 space-y-3">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-sm font-medium text-neutral-800 py-1.5">{l.label}</a>
          ))}
          <div className="flex gap-3 pt-2">
            <Link to="/register" className="flex-1 text-center text-sm font-semibold text-neutral-900 px-4 py-2.5 rounded-xl border border-neutral-300">Registrati</Link>
            <Link to="/login" className={`${btn} flex-1 text-sm px-5 py-2.5`}>Accedi</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ onApply }) {
  return (
    <section id="top" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full blur-3xl opacity-30" style={{ background: NEON }} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 bg-neutral-100 px-3 py-1.5 rounded-full">Hub servizi · Italia</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-neutral-900 mt-6 leading-[1.05]">
            Tutti i tuoi servizi. <span className="px-2 rounded-lg" style={{ backgroundColor: NEON }}>Un unico passpartù.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-6 max-w-xl leading-relaxed">
            Un solo hub centralizzato con login unificato. Accedi a tutti i servizi dell'ecosistema Mypasspartu.it — in un unico posto.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/login" data-testid="hero-login-button" className={`${btn} text-base px-7 py-3.5`}>Accedi al tuo Hub <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/register" data-testid="hero-register-button" className={`${btn} text-base px-7 py-3.5`}>Registrati</Link>
            <button onClick={onApply} data-testid="hero-apply-cta-button" className={`${btn} text-base px-7 py-3.5`}>Candida il tuo servizio</button>
          </div>
        </div>
        <div className="relative">
          <div className="rounded-3xl overflow-hidden border border-neutral-200 shadow-2xl">
            <img src={HERO_IMG} alt="Ecosistema di servizi Mypasspartu.it" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-neutral-200 shadow-xl px-5 py-4 max-w-[220px]">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Un solo accesso</div>
            <div className="font-mono-data font-bold text-2xl text-neutral-900 mt-0.5">Tutti i servizi</div>
            <div className="text-xs text-neutral-500 mt-1">dell'ecosistema Mypasspartu.it</div>
          </div>
          <div className="absolute -top-4 right-6 bg-[#E3FF00] text-neutral-900 rounded-2xl shadow-xl px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border border-neutral-900/10">
            <span className="h-2 w-2 rounded-full bg-neutral-900" /> SSO attivo
          </div>
        </div>
      </div>
    </section>
  );
}

const PILLS = [
  { icon: KeyRound, title: "Un solo login (SSO)", desc: "Basta decine di credenziali separate per portali, utility e servizi energetici." },
  { icon: Zap, title: "Accesso immediato", desc: "Attivi e raggiungi ogni servizio dell'ecosistema in un clic, senza procedure separate." },
  { icon: Layers, title: "Multi-tenant & branding", desc: "Assegna delegati, ruoli per collaboratori e personalizza il tenant per il tuo team." },
  { icon: Boxes, title: "Ecosistema aperto", desc: "Nuovi servizi integrati costantemente grazie alla valutazione attiva delle candidature." },
];

function ValuePills() {
  return (
    <section id="cos-e" className="py-20 sm:py-24 bg-[#E3FF00]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900 max-w-2xl leading-tight">Perché scegliere Mypasspartu.it</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PILLS.map((p, i) => (
            <div key={i} className="bg-white border border-neutral-900/10 rounded-2xl p-6 hover:-translate-y-1 transition-transform shadow-[0_2px_0_rgba(0,0,0,0.06)]" data-testid={`value-pill-${i}`}>
              <div className="h-11 w-11 rounded-xl grid place-items-center bg-neutral-900"><p.icon className="h-5 w-5 text-[#E3FF00]" /></div>
              <h3 className="font-display font-bold text-lg text-neutral-900 mt-5">{p.title}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", name: "Crea il tuo account", desc: "Registrati in pochi minuti e, se ti serve, invita i tuoi collaboratori." },
  { n: "02", name: "Attiva i servizi", desc: "Scegli e attiva i servizi dell'ecosistema che ti servono, quando ti servono." },
  { n: "03", name: "Accedi a tutti i servizi", desc: "Un solo clic per entrare in Home Smart, attivare Infinity o comparare tariffe con Comparapro." },
];

function HowItWorks() {
  return (
    <section id="come-funziona" className="py-20 sm:py-28 bg-neutral-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="rounded-3xl overflow-hidden border border-neutral-200 shadow-xl order-last lg:order-first">
          <img src={WORK_IMG} alt="Appartamento turistico gestito" className="w-full h-[440px] object-cover" />
        </div>
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900">Come funziona</h2>
          <div className="mt-10 space-y-8">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-5" data-testid={`how-step-${s.n}`}>
                <span className="font-mono-data font-bold text-lg h-12 w-12 shrink-0 rounded-xl grid place-items-center text-neutral-900" style={{ backgroundColor: NEON }}>{s.n}</span>
                <div>
                  <h3 className="font-display font-bold text-xl text-neutral-900">{s.name}</h3>
                  <p className="text-neutral-600 mt-1.5 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ApplySection() {
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", phone: "", website: "", service_name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const inp = "w-full bg-white border border-neutral-300 rounded-lg px-3.5 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#E3FF00] focus:border-neutral-400";
  const submit = async () => {
    if (!f.company_name.trim() || !f.service_name.trim() || !f.description.trim() || !f.email.trim())
      return toast.error("Compila azienda, email, nome servizio e descrizione");
    setSaving(true);
    try { await api.post("/public/service-submissions", f); setSent(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); setSaving(false); }
  };
  return (
    <section id="candida" className="py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="bg-[#E3FF00] rounded-3xl p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900 leading-tight">Hai un servizio da offrire?</h2>
            <p className="text-neutral-900/80 mt-5 leading-relaxed">Candida la tua soluzione per entrare nel catalogo ufficiale Mypasspartu.it e raggiungere i nostri utenti in tutta Italia.</p>
            <div className="mt-6 flex items-center gap-2 text-neutral-900/70 text-sm"><Check className="h-4 w-4 text-neutral-900" /> Valutazione gratuita e senza impegno</div>
          </div>
          <div className="bg-white rounded-2xl p-6" data-testid="public-apply-form">
            {sent ? (
              <div className="text-center py-8 space-y-4" data-testid="apply-success-box">
                <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto" style={{ backgroundColor: NEON }}><Check className="h-8 w-8 text-neutral-900" /></div>
                <h3 className="font-display text-xl font-bold text-neutral-900">Candidatura inviata con successo!</h3>
                <p className="text-sm text-neutral-600 max-w-sm mx-auto">Valuteremo il tuo servizio e ti faremo sapere. Ti abbiamo inviato una conferma via email.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <input className={inp} placeholder="Nome azienda *" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="apply-company-input" />
                <input className={inp} placeholder="Referente" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="apply-contact-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input className={inp} type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="apply-email-input" />
                  <input className={inp} placeholder="Telefono" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="apply-phone-input" />
                </div>
                <input className={inp} placeholder="Sito web (se esiste)" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="apply-website-input" />
                <input className={inp} placeholder="Nome del servizio proposto *" value={f.service_name} onChange={(e) => setF({ ...f, service_name: e.target.value })} data-testid="apply-service-input" />
                <textarea className={inp + " min-h-28"} placeholder="Descrivi il servizio e il valore per i tuoi utenti *" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="apply-description-input" />
                <button onClick={submit} disabled={saving} className={`${btn} w-full py-3.5`} data-testid="apply-submit-button">{saving ? "Invio..." : "Invia candidatura servizio"}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="public-footer" className="bg-[#E3FF00] text-neutral-900/70 py-14">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-8">
        <div className="max-w-sm">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-2xl grid place-items-center font-display font-extrabold text-lg text-[#E3FF00] bg-neutral-900">MP</span>
            <span className="font-display font-extrabold text-lg text-neutral-900">Mypasspartu.it</span>
          </div>
          <p className="text-sm mt-4 leading-relaxed text-neutral-900/70">L'ecosistema di servizi digitali, accessibili con un unico account.</p>
          <a href="mailto:info@mypasspartu.it" className="text-sm mt-4 inline-block text-neutral-900 font-medium hover:underline">info@mypasspartu.it</a>
        </div>
        <div className="flex gap-16">
          <div className="space-y-2.5 text-sm">
            <div className="font-semibold text-neutral-900 mb-3">Piattaforma</div>
            <Link to="/login" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Accedi</Link>
            <Link to="/register" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Registrati</Link>
            <a href="#partner" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Servizi</a>
            <a href="#candida" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Candida servizio</a>
          </div>
          <div className="space-y-2.5 text-sm">
            <div className="font-semibold text-neutral-900 mb-3">Legale</div>
            <Link to="/privacy" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Cookie Policy</Link>
            <Link to="/termini" className="block text-neutral-900/70 hover:text-neutral-900 transition-colors">Termini</Link>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-10 pt-8 border-t border-neutral-900/15 text-xs text-neutral-900/70">© 2026 Mypasspartu.it. Tutti i diritti riservati.</div>
    </footer>
  );
}

function Partners() {
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [profiles, setProfiles] = useState({});
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/public/services").then(({ data }) => { if (Array.isArray(data) && data.length) setServices(data); }).catch(() => {});
    api.get("/public/service-profiles").then(({ data }) => setProfiles(Object.fromEntries(data.map((p) => [p.key, p])))).catch(() => {});
  }, []);
  const openInfo = (s) => setInfo(profiles[s.key] || { ...s, short_description: s.description, description: "" });
  const items = [...services, ...services];
  return (
    <section id="partner" className="py-20 sm:py-24 bg-white border-y border-neutral-200">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">Ecosistema</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900 mt-3">I nostri partner</h2>
        <p className="text-neutral-600 mt-3 max-w-xl mx-auto">I servizi integrati nella piattaforma Mypasspartu.it.</p>
      </div>
      <div className="mt-12 marquee-group marquee-mask overflow-hidden" data-testid="partners-marquee">
        <div className="flex gap-5 animate-marquee-x px-2.5" style={{ "--marquee-duration": "42s" }}>
          {items.map((s, i) => (
            <button type="button" key={s.key + i} onClick={() => openInfo(s)} data-testid={`partner-card-${s.key}-${i}`}
              className="shrink-0 w-56 bg-white rounded-2xl border border-neutral-200 p-6 flex flex-col items-center text-center shadow-[0_2px_0_rgba(0,0,0,0.05)] cursor-pointer hover:border-neutral-900 hover:-translate-y-1 transition-[transform,border-color]">
              <div className="h-20 w-full grid place-items-center overflow-hidden rounded-xl" style={s.logo_bg ? { backgroundColor: s.logo_bg } : undefined}>
                {s.logo_url ? <img src={s.logo_url} alt={s.name} className={`object-contain ${s.logo_bg ? "max-h-12 max-w-[80%]" : "max-h-20 max-w-[85%]"}`} />
                  : <span className="font-display font-extrabold text-3xl text-neutral-300">{s.name.slice(0, 1)}</span>}
              </div>
              <div className="font-display font-bold text-neutral-900 mt-4">{s.name}</div>
              <div className="text-xs text-neutral-500 mt-1">Scopri di più</div>
            </button>
          ))}
        </div>
      </div>
      {info && <ServiceInfoModal profile={info} onClose={() => setInfo(null)}
        action={<Link to="/register" className={`${btn} w-full py-3`} data-testid="service-info-cta">Registrati per attivarlo <ArrowRight className="h-4 w-4" /></Link>} />}
    </section>
  );
}

const REVIEWS = [
  { text: "Finalmente un unico accesso per tutti i servizi. Risparmio ore ogni settimana.", name: "Giulia R.", role: "Firenze" },
  { text: "Tutti i servizi in un unico posto: una comodità enorme.", name: "Marco T.", role: "Roma" },
  { text: "Con Infinity ho abbattuto i costi di luce e gas.", name: "Elena P.", role: "Napoli" },
  { text: "Comparapro mi ha fatto trovare tariffe migliori in pochi minuti.", name: "Davide L.", role: "Bologna" },
  { text: "Un solo login per tutto: comodissimo e velocissimo.", name: "Sara M.", role: "Siena" },
  { text: "Supporto rapido e piattaforma chiarissima. La consiglio a tutti.", name: "Luca B.", role: "Milano" },
];

function Reviews() {
  const items = [...REVIEWS, ...REVIEWS];
  return (
    <section id="recensioni" className="py-20 sm:py-24 bg-neutral-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">Dicono di noi</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900 mt-3">Recensioni</h2>
        <p className="text-neutral-600 mt-3 max-w-xl mx-auto">Cosa pensano gli utenti che usano Mypasspartu.it.</p>
      </div>
      <div className="mt-12 marquee-group marquee-mask overflow-hidden" data-testid="reviews-marquee">
        <div className="flex gap-5 animate-marquee-x px-2.5" style={{ "--marquee-duration": "55s" }}>
          {items.map((r, i) => (
            <div key={i} className="shrink-0 w-80 bg-white rounded-2xl border border-neutral-200 p-6 text-left shadow-[0_2px_0_rgba(0,0,0,0.05)]">
              <div className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" style={{ color: NEON }} />)}</div>
              <p className="text-neutral-700 mt-4 leading-relaxed text-sm">“{r.text}”</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full grid place-items-center font-display font-bold text-neutral-900" style={{ backgroundColor: NEON }}>{r.name.slice(0, 1)}</div>
                <div>
                  <div className="font-semibold text-neutral-900 text-sm">{r.name}</div>
                  <div className="text-xs text-neutral-500">{r.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const scrollToApply = () => document.getElementById("candida")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="bg-[#FDFDFC] min-h-screen" data-testid="public-landing">
      <Navbar />
      <Hero onApply={scrollToApply} />
      <Partners />
      <ValuePills />
      <Reviews />
      <HowItWorks />
      <ApplySection />
      <Footer />
      <CookieConsent />
    </div>
  );
}
